import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import {
  createdSurveyRefSchema,
  paginatedSurveyRespondentsSchema,
  paginatedSurveysSchema,
  submittedSurveyRefSchema,
  surveyDetailSchema,
  surveySubmissionSummarySchema,
  type CreatedSurveyRef,
  type CreateSurveyPayload,
  type PaginatedSurveyRespondents,
  type PaginatedSurveys,
  type SubmitSurveyPayload,
  type SubmittedSurveyRef,
  type SurveyDetail,
  type SurveyListParams,
  type SurveySubmissionFilters,
  type SurveySubmissionListParams,
  type SurveySubmissionSummary,
  type UpdateSurveyPayload,
} from '../types/survey.types';

export const surveyQueryKeys = {
  all: () => ['surveys'] as const,
  lists: () => [...surveyQueryKeys.all(), 'list'] as const,
  listInfinite: (params: Omit<SurveyListParams, 'page'>) =>
    [...surveyQueryKeys.lists(), 'infinite', params] as const,
  detail: (id: number) => [...surveyQueryKeys.all(), 'detail', id] as const,
  submissions: (id: number) => [...surveyQueryKeys.all(), 'submissions', id] as const,
  submissionsInfinite: (id: number, params: Omit<SurveySubmissionListParams, 'page'>) =>
    [...surveyQueryKeys.submissions(id), 'infinite', params] as const,
  submissionsSummary: (id: number, params: SurveySubmissionFilters) =>
    [...surveyQueryKeys.submissions(id), 'summary', params] as const,
};

export const surveyListInfiniteQueryOptions = (params: Omit<SurveyListParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: surveyQueryKeys.listInfinite(params),
    queryFn: async ({ pageParam }): Promise<PaginatedSurveys> => {
      const res = await apiClient.get(API.survey.list, {
        params: { ...params, page: pageParam },
      });
      return paginatedSurveysSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createSurvey(payload: CreateSurveyPayload): Promise<CreatedSurveyRef> {
  const res = await apiClient.post(API.survey.create, payload);
  return createdSurveyRefSchema.parse(res.data);
}

export async function updateSurvey(id: number, payload: UpdateSurveyPayload): Promise<void> {
  await apiClient.patch(API.survey.update(id), payload);
}

export async function submitSurveyAnswers(
  id: number,
  payload: SubmitSurveyPayload,
): Promise<SubmittedSurveyRef> {
  const res = await apiClient.post(API.survey.submissions(id), payload);
  return submittedSurveyRefSchema.parse(res.data);
}

export const surveyDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: surveyQueryKeys.detail(id),
    queryFn: async (): Promise<SurveyDetail> => {
      const res = await apiClient.get(API.survey.detail(id));
      const parseResult = surveyDetailSchema.safeParse(res.data);
      if (!parseResult.success) {
        console.error('Survey detail schema mismatch:', parseResult.error);
        throw parseResult.error;
      }
      return parseResult.data;
    },
    staleTime: STALE_TIME,
  });

export const surveySubmissionsInfiniteQueryOptions = (
  surveyId: number,
  params: Omit<SurveySubmissionListParams, 'page'>,
) =>
  infiniteQueryOptions({
    queryKey: surveyQueryKeys.submissionsInfinite(surveyId, params),
    queryFn: async ({ pageParam }): Promise<PaginatedSurveyRespondents> => {
      const res = await apiClient.get(API.survey.submissions(surveyId), {
        params: { ...params, page: pageParam },
      });
      return paginatedSurveyRespondentsSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const surveySubmissionSummaryQueryOptions = (
  surveyId: number,
  params: SurveySubmissionFilters,
) =>
  queryOptions({
    queryKey: surveyQueryKeys.submissionsSummary(surveyId, params),
    queryFn: async (): Promise<SurveySubmissionSummary> => {
      const res = await apiClient.get(API.survey.submissionsSummary(surveyId), { params });
      return surveySubmissionSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
